import React, { useEffect, useRef, useState } from "react";
import { createVideoCallSignaling } from "../utils/videoCallSignaling";
import { connectSocket } from "../socket";   // ✅ ADD THIS IMPORT
import useContacts from "../hooks/useContacts";

export default function VideoCall({
  localUser, remoteUser, onEnd, isCaller, incomingOffer, contacts: externalContacts = []
}) {
  const { contacts: fetchedContacts } = useContacts();
  const contacts = externalContacts.length > 0 ? externalContacts : fetchedContacts;
  
  const localVideo     = useRef(null);
  const remoteVideo    = useRef(null);
  const pcRef          = useRef(null);
  const localStreamRef = useRef(null);
  const signalingRef   = useRef(null);
  const pendingICE     = useRef([]);
  const isEndedRef     = useRef(false);
  const timerRef       = useRef(null);

  const [isMuted,   setIsMuted]   = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [duration,  setDuration]  = useState(0);
  const [error,     setError]     = useState("");
  const [participants, setParticipants] = useState([remoteUser]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedContactsToInvite, setSelectedContactsToInvite] = useState([]);

  // ── endCall (safe, runs once) ─────────────────────────────────────────────
  function endCallOnce() {
    if (isEndedRef.current) return;
    isEndedRef.current = true;

    clearInterval(timerRef.current);
    timerRef.current = null;

    if (remoteVideo.current) remoteVideo.current.srcObject = null;
    if (localVideo.current)  localVideo.current.srcObject  = null;

    if (pcRef.current) {
      pcRef.current.ontrack = null;
      pcRef.current.onicecandidate = null;
      pcRef.current.close();
      pcRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    pendingICE.current = [];

    if (signalingRef.current?.cleanup) {
      signalingRef.current.cleanup();
    }

    if (onEnd) onEnd();
  }

  // ── ✅ KEY FIX: Listen for call_end from the remote user ─────────────────
  useEffect(() => {
    const socket = connectSocket();

    const handleRemoteEnd = ({ from }) => {
      if (from?.toLowerCase() === remoteUser?.toLowerCase()) {
        console.log("📴 Remote user ended the video call");
        endCallOnce();
      }
    };

    socket.on("call_end", handleRemoteEnd);

    return () => {
      socket.off("call_end", handleRemoteEnd);
    };
  }, [remoteUser]);

  // ── ✅ Resume media playback on user interaction (autoplay policy workaround) ──
  useEffect(() => {
    const resumeMedia = () => {
      const video = remoteVideo.current;
      if (video && video.paused) {
        console.log("📹 User interacted - attempting to resume video...");
        video.play().catch(err => {
          console.error("Still cannot play video:", err);
        });
      }
    };

    // Listen for any click on the video container to resume playback
    const videoContainer = document.querySelector(".video-call-container");
    if (videoContainer) {
      videoContainer.addEventListener("click", resumeMedia);
      return () => {
        videoContainer.removeEventListener("click", resumeMedia);
      };
    }
  }, []);

  // ── WebRTC setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    const signaling = createVideoCallSignaling(localUser, remoteUser);
    signalingRef.current = signaling;

    async function startCall() {
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          video: true, audio: true
        });
        localStreamRef.current = localStream;
        if (localVideo.current) localVideo.current.srcObject = localStream;

        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.cloudflare.com:3478" },
            { urls: "turn:openrelay.metered.ca:80",  username: "openrelayproject", credential: "openrelayproject" },
            { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" }
          ]
        });
        pcRef.current = pc;

        localStream.getTracks().forEach(t => pc.addTrack(t, localStream));

        pc.ontrack = (event) => {
          console.log("🎥 Received remote track:", event.track?.kind, "streams:", event.streams.length);
          const remoteStream = event.streams[0];
          if (remoteVideo.current) {
            remoteVideo.current.srcObject = remoteStream;
            console.log("📹 Setting remote video stream");
            console.log("   Video element state - paused:", remoteVideo.current.paused, "readyState:", remoteVideo.current.readyState);
            
            // ✅ Start timer immediately when we get remote track (fallback)
            if (!timerRef.current) {
              console.log("⏱️  Starting timer (remote track received)");
              timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
            }
            
            // ✅ Try to play video - detailed error handling
            setTimeout(() => {
              const playPromise = remoteVideo.current.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    console.log("✅ Video playing successfully");
                  })
                  .catch((err) => {
                    console.error("❌ Error playing video:", err.name, err.message);
                    if (err.name === "NotAllowedError") {
                      console.log("💡 Autoplay blocked by browser. Video ready but waiting for user interaction.");
                    }
                  });
              }
            }, 100);
          } else {
            console.warn("⚠️  Remote video ref is null!");
          }
        };

        pc.onicecandidate = (e) => {
          if (e.candidate) signaling.sendICE(e.candidate);
        };

        pc.onconnectionstatechange = () => {
          console.log("🎥 Connection state:", pc.connectionState);
          console.log("   iceConnectionState:", pc.iceConnectionState);
          console.log("   iceGatheringState:", pc.iceGatheringState);
          
          if ((pc.connectionState === "connected" || pc.connectionState === "completed") && !timerRef.current) {
            console.log("✅ Video call connected, starting timer");
            timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
          } else if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
            console.error("❌ Video connection failed or disconnected:", pc.connectionState);
            endCallOnce();
          }
        };

        // ✅ Also monitor ICE connection state
        pc.oniceconnectionstatechange = () => {
          console.log("🧊 ICE connection state:", pc.iceConnectionState);
          if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
            if (!timerRef.current) {
              console.log("⏱️  Starting timer (ICE connected)");
              timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
            }
          } else if (pc.iceConnectionState === "failed") {
            console.error("❌ ICE connection failed");
            endCallOnce();
          }
        };

        signaling.onICE = async (candidate) => {
          try {
            if (pc.remoteDescription) {
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } else {
              pendingICE.current.push(candidate);
            }
          } catch (e) { console.error("ICE error:", e); }
        };

        const flushPendingICE = async () => {
          const candidates = pendingICE.current.splice(0);
          for (const c of candidates) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(c));
            } catch (e) {
              console.error("Error adding pending ICE candidate:", e);
            }
          }
        };

        // signaling.onEnd — belt-and-suspenders with the useEffect above
        signaling.onEnd = () => endCallOnce();

        if (isCaller) {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          signaling.sendOffer(offer);
          signaling.onAnswer = async (answer) => {
            try {
              await pc.setRemoteDescription(new RTCSessionDescription(answer));
              await flushPendingICE();
            } catch (err) {
              console.error("Error setting remote description:", err);
            }
          };
        } else if (incomingOffer) {
          await pc.setRemoteDescription(new RTCSessionDescription(incomingOffer));
          await flushPendingICE();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          signaling.sendAnswer(answer);
        }

      } catch (err) {
        console.error("Video call error:", err);
        setError("Could not start video call: " + err.message);
      }
    }

    startCall();
    return () => endCallOnce();
  }, [localUser, remoteUser]);

  // ── Invite participants to conference ──────────────────────────────────────
  const inviteToCall = async (emailsToInvite) => {
    const socket = connectSocket();
    const callId = `${localUser}_${Date.now()}`;
    
    emailsToInvite.forEach(email => {
      socket.emit("conference_invite", {
        to: email,
        from: localUser,
        participants: [localUser, remoteUser, ...emailsToInvite],
        callId: callId,
        type: "video"
      });
    });

    setParticipants(prev => {
      const updated = Array.from(new Set([...prev, ...emailsToInvite]));
      return updated;
    });
    
    setSelectedContactsToInvite([]);
    setShowInviteModal(false);
  };

  // ── Listen for conference join notifications ──────────────────────────────
  useEffect(() => {
    const socket = connectSocket();
    
    const handleConferenceJoin = ({ from }) => {
      setParticipants(prev => {
        if (prev.includes(from)) return prev;
        return [...prev, from];
      });
    };

    const handleConferenceLeave = ({ from }) => {
      setParticipants(prev => prev.filter(p => p !== from));
    };

    socket.on("conference_participant_joined", handleConferenceJoin);
    socket.on("conference_participant_left", handleConferenceLeave);

    return () => {
      socket.off("conference_participant_joined", handleConferenceJoin);
      socket.off("conference_participant_left", handleConferenceLeave);
    };
  }, []);

  // ── Controls ──────────────────────────────────────────────────────────────
  function toggleMute() {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = isMuted;     // flip: if currently muted, enable
    setIsMuted(prev => !prev);
  }

  function toggleCamera() {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = cameraOff;   // flip
    setCameraOff(prev => !prev);
  }

  async function toggleScreenShare() {
    const pc = pcRef.current;
    if (!pc) return;
    try {
      if (!isSharing) {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack  = screenStream.getVideoTracks()[0];
        const sender = pc.getSenders().find(s => s.track?.kind === "video");
        if (sender) sender.replaceTrack(screenTrack);
        if (localVideo.current) localVideo.current.srcObject = screenStream;
        screenTrack.onended = () => toggleScreenShare();
        setIsSharing(true);
      } else {
        const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const camTrack  = camStream.getVideoTracks()[0];
        const sender = pc.getSenders().find(s => s.track?.kind === "video");
        if (sender) sender.replaceTrack(camTrack);
        if (localVideo.current) localVideo.current.srcObject = camStream;
        localStreamRef.current = camStream;
        setIsSharing(false);
      }
    } catch (err) { console.error("Screen share error:", err); }
  }

  function formatTime(sec) {
    return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  }

  return (
    <div className="video-call-container">
      <video ref={remoteVideo} autoPlay playsInline className="remote-video" />
      <video ref={localVideo}  autoPlay muted playsInline className="local-video" />

      <div className="call-duration">{formatTime(duration)}</div>

      {/* Conference Participants Display */}
      {participants.length > 1 && (
        <div style={{
          position: "absolute",
          top: 16,
          right: 16,
          background: "rgba(15,23,42,0.9)",
          border: "1px solid rgba(59,130,246,0.3)",
          borderRadius: 12,
          padding: "12px 16px",
          maxWidth: 200,
          maxHeight: 150,
          overflowY: "auto",
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#3b82f6",
            marginBottom: 8,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
            📹 Conference ({participants.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {participants.map((p, i) => (
              <div key={i} style={{
                fontSize: 11,
                color: "#e2e8f0",
                padding: "4px 8px",
                background: "rgba(59,130,246,0.1)",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 6
              }}>
                <span style={{ fontSize: 10, color: "#22c55e" }}>●</span>
                {p.split("@")[0] || p}
              </div>
            ))}
          </div>
        </div>
      )}

    <div className="video-call-footer">
  <button
    className={`video-btn ${isMuted ? "active" : ""}`}
    onClick={toggleMute}
    title="Toggle Microphone"
  >
    <i className={`fa-solid ${isMuted ? "fa-microphone-slash" : "fa-microphone"}`} />
  </button>

  <button
    className={`video-btn ${cameraOff ? "active" : ""}`}
    onClick={toggleCamera}
    title="Toggle Camera"
  >
    <i className={`fa-solid ${cameraOff ? "fa-video-slash" : "fa-video"}`} />
  </button>

  <button style={{ display: "none" }}
    className={`video-btn ${isSharing ? "active" : ""}`}
    onClick={toggleScreenShare}
  >
    <i className="fa-solid fa-desktop" />
  </button>

  {/* Add Participant Button */}
  <button
    className="video-btn"
    onClick={() => setShowInviteModal(true)}
    title="Add Participant to Conference"
    style={{
      background: "rgba(34,197,94,0.2)",
      color: "#22c55e",
    }}
  >
    <i className="fa-solid fa-user-plus" />
  </button>

  <button
    className="video-btn end-call-btn"
    onClick={() => {
      const socket = connectSocket();
      socket.emit("call_end", { to: remoteUser });
      participants.forEach(p => {
        if (p !== localUser && p !== remoteUser) {
          socket.emit("conference_end", { to: p });
        }
      });
      endCallOnce();
    }}
    title="End Call"
  >
    <i className="fa-solid fa-phone-slash" />
  </button>
</div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={() => setShowInviteModal(false)}
        >
          <div style={{
            background: "#0f172a",
            border: "1px solid rgba(59,130,246,0.3)",
            borderRadius: 16,
            padding: 24,
            maxWidth: 420,
            width: "90%",
            maxHeight: "80vh",
            overflow: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}>
              <h3 style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#f1f5f9"
              }}>
                Add to Conference
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  fontSize: 24,
                  cursor: "pointer",
                  padding: 0,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Contact List */}
            <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}>
              {contacts.filter(c => {
                const email = c.emails?.[0] || c.email;
                return !participants.includes(email);
              }).length === 0 ? (
                <div style={{
                  textAlign: "center",
                  color: "#64748b",
                  padding: "24px 0",
                  fontSize: 13
                }}>
                  No more contacts to add
                </div>
              ) : (
                contacts.filter(c => {
                  const email = c.emails?.[0] || c.email;
                  return email && !participants.includes(email);
                }).map((contact) => {
                  const email = contact.emails?.[0] || contact.email;
                  const isSelected = selectedContactsToInvite.includes(email);
                  
                  return (
                    <div
                      key={email}
                      onClick={() => {
                        setSelectedContactsToInvite(prev =>
                          isSelected
                            ? prev.filter(e => e !== email)
                            : [...prev, email]
                        );
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 12px",
                        marginBottom: 6,
                        borderRadius: 10,
                        background: isSelected ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.05)",
                        border: `1px solid ${isSelected ? "rgba(59,130,246,0.5)" : "rgba(59,130,246,0.2)"}`,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="chkcss"
                        style={{
                          cursor: "pointer",
                          marginRight: 4
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#f1f5f9",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {contact.name || email.split("@")[0]}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: "#64748b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {email}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: "10px 16px",
                  background: "rgba(148,163,184,0.12)",
                  border: "1px solid rgba(148,163,184,0.3)",
                  borderRadius: 8,
                  color: "#cbd5e1",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedContactsToInvite.length > 0) {
                    inviteToCall(selectedContactsToInvite);
                  }
                }}
                disabled={selectedContactsToInvite.length === 0}
                style={{
                  padding: "10px 16px",
                  background: selectedContactsToInvite.length > 0 ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.1)",
                  border: "1px solid rgba(34,197,94,0.3)",
                  borderRadius: 8,
                  color: selectedContactsToInvite.length > 0 ? "#22c55e" : "#475569",
                  cursor: selectedContactsToInvite.length > 0 ? "pointer" : "not-allowed",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Invite {selectedContactsToInvite.length > 0 ? `(${selectedContactsToInvite.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="call-error">{error}</div>}
    </div>
  );
}
